import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@alchemer/ui/components/breadcrumb';
import { CreatorGuard } from '@/components/CreatorGuard/CreatorGuard';
import { DatasetEditor } from '@/components/DatasetEditor/DatasetEditor';
import PageNotFound from '@/components/Error/PageNotFound/PageNotFound';
import { FeatureFlagGuard } from '@/components/FeatureFlagGuard/FeatureFlagGuard';
import { FEATURE_FLAGS } from '@/config/features';
import { tw } from '@/lib/tailwind';

/**
 * PRIMARY ROUTE: Dataset Editor
 *
 * Pure Alchemer functionality for configuring the columns of a single dataset
 * (rename, response/column type, visibility, merge). Reached from the Datasets
 * list. Currently renders mock data; column-config API wiring is a later step.
 *
 * URL Pattern: /dashboard/dataset-editor/{guid}
 */
export const Route = createFileRoute('/dashboard/_primary-routes/dataset-editor/$guid')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const { isAuthenticated, isLoading } = context.auth;

    if (!isLoading && !isAuthenticated) {
      return; // AuthProvider will handle login redirect
    }
  },
});

function RouteComponent() {
  const { guid } = Route.useParams();
  const navigate = useNavigate();

  const handleClose = () => navigate({ to: '/dashboard/datasets' });

  return (
    <FeatureFlagGuard
      flag={FEATURE_FLAGS.DATASETS_2_0}
      redirect={
        <Navigate
          to="/dashboard/data-source/$tab"
          params={{ tab: 'datasets' }}
          replace
        />
      }
    >
      <CreatorGuard fallback={<PageNotFound />}>
        <div className={tw('h-[calc(100vh-var(--dashboard-menu-height))] w-full overflow-auto bg-white p-6')}>
          <Breadcrumb className="mb-2">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={handleClose}
                  className={tw(
                    'cursor-pointer opacity-50 transition-opacity duration-150 ease-in-out hover:opacity-100',
                  )}
                >
                  Datasets
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Dataset Editor</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h3 className="mb-5 text-xl font-semibold">Dataset Editor</h3>

          <DatasetEditor
            guid={guid}
            onClose={handleClose}
          />
        </div>
      </CreatorGuard>
    </FeatureFlagGuard>
  );
}
